'use client';

import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Check } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

import { ProcessorDetails, useProcessorById, useUpdateProcessor } from '@/lib/api/processors';
import { ProcessorDryModeField } from '../../components/processor-dry-mode-field';
import { ErrorDisplay } from '@/components/ui/error-display';
import { parseTRPCError } from '@/lib/utils/trpc-error-utils';
import { useNavigate } from 'react-router-dom';
import { ProcessorEditHeader } from '../../components/processor-edit-header';
import { useProcessorEditId } from '@/app/processors/hooks/use-processor-edit-id';
import { useProcessorEditExit } from '@/app/processors/hooks/use-processor-edit-exit';
import { ConvesioAutoConnectSection, type ConvesioConnectResult } from './convesio-auto-connect-section';

interface ConvesioEditPageProps {
  processor: ProcessorDetails;
}

const ConvesioEditPageContainerInner = () => {
  const { t } = useTranslation('processors');
  const id = useProcessorEditId();
  const { data, isLoading, error } = useProcessorById(id!);

  if (isLoading) {
    return (
      <div className="container mx-auto py-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 w-1/3 rounded bg-gray-200"></div>
          <div className="space-y-4">
            <div className="h-32 rounded bg-gray-200"></div>
            <div className="h-32 rounded bg-gray-200"></div>
            <div className="h-32 rounded bg-gray-200"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return <ErrorDisplay error={parseTRPCError(error)} />;
  }

  if (!data) {
    return <div className="container mx-auto py-8">{t('processorNotFound')}</div>;
  }

  return <ConvesioEditPage processor={data} />;
};

const SUPPORTED_COUNTRIES = ['US', 'CA', 'GB', 'AU', 'NZ', 'IE', 'DE', 'FR', 'ES', 'IT', 'NL', 'BE'];

const ConvesioEditPage = ({ processor }: ConvesioEditPageProps) => {
  const { t } = useTranslation('processors');
  const navigate = useNavigate();
  const processorExit = useProcessorEditExit();
  const { toast } = useToast();

  const isAlreadyConfigured = !!(processor.options as any)?.secretKey;
  const [tab, setTab] = useState<'auto' | 'manual'>(isAlreadyConfigured ? 'manual' : 'auto');
  const [validationError, setValidationError] = useState<string | null>(null);
  const updateProcessorMutation = useUpdateProcessor();

  const [formData, setFormData] = useState({
    name: processor.name || '',
    enabled: processor.enabled ?? true,
    testMode: (processor.options as any)?.testMode ?? false,
    dryMode: (processor.options as any)?.dryMode === true,
    secretKey: (processor.options as any)?.secretKey || '',
    integrationName: (processor.options as any)?.integrationName || '',
    webhookSecret: (processor.options as any)?.webhookSecret || '',
  });

  const saveProcessor = async (overrides?: Partial<typeof formData>) => {
    const data = { ...formData, ...overrides };
    if (!data.secretKey.trim()) {
      setValidationError(t('secretKeyRequired'));
      return;
    }
    if (!data.integrationName.trim()) {
      setValidationError(t('integrationNameRequired'));
      return;
    }
    setValidationError(null);
    try {
      await updateProcessorMutation.mutateAsync({
        processorId: processor.id,
        updatedData: {
          type: 'convesio',
          name: data.name,
          enabled: data.enabled,
          options: {
            testMode: data.testMode,
            dryMode: data.dryMode,
            secretKey: data.secretKey,
            integrationName: data.integrationName,
            webhookSecret: data.webhookSecret,
          },
        } as any,
      });
      toast({ description: t('processorUpdated') });
      if (processorExit.isEmbed) {
        processorExit.exitAfterSave();
        return;
      }
      navigate(-1);
    } catch (error) {
      console.error('Error saving processor:', error);
      toast({ description: t('failedToSave'), variant: 'destructive' });
    }
  };

  const handleAutoConnectSuccess = (result: ConvesioConnectResult) => {
    const next = {
      ...formData,
      secretKey: result.secretKey,
      integrationName: result.integrationName,
      webhookSecret: result.webhookSecret,
      testMode: result.testMode,
    };
    setFormData(next);
    setTab('manual');
    saveProcessor(next);
  };

  return (
    <div className="container mx-auto py-8">
      <ProcessorEditHeader
        processorId={processor.id}
        title={t('convesio.editTitle')}
        onCancel={() => (processorExit.isEmbed ? processorExit.exitCancel() : navigate(-1))}
        onSave={() => saveProcessor()}
        isSaving={updateProcessorMutation.isPending}
      />

      {validationError && (
        <Alert variant="destructive" className="mb-6">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{validationError}</AlertDescription>
        </Alert>
      )}

      {isAlreadyConfigured && (
        <Alert className="mb-6 border-green-200 bg-green-50">
          <Check className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-700">
            ConvesioPay is connected. Integration: <strong>{formData.integrationName || '—'}</strong>
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-6">
        {/* General Settings */}
        <Card>
          <CardHeader><CardTitle>{t('generalSettings')}</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="name">{t('name')}</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('enterProcessorName')}
              />
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="enabled"
                checked={formData.enabled}
                onCheckedChange={(checked) => setFormData({ ...formData, enabled: checked === true })}
              />
              <Label htmlFor="enabled">{t('enabled')}</Label>
            </div>
          </CardContent>
        </Card>

        {/* Credentials — tabbed */}
        <Tabs value={tab} onValueChange={(v) => setTab(v as 'auto' | 'manual')}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="auto">{t('convesio.tabs.autoConnect')}</TabsTrigger>
            <TabsTrigger value="manual">{t('convesio.tabs.manualSetup')}</TabsTrigger>
          </TabsList>

          <TabsContent value="auto" className="mt-4">
            <ConvesioAutoConnectSection
              testMode={formData.testMode}
              onTestModeChange={(v) => setFormData({ ...formData, testMode: v })}
              onSuccess={handleAutoConnectSuccess}
              isDisabled={updateProcessorMutation.isPending}
            />
          </TabsContent>

          <TabsContent value="manual" className="mt-4 space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>{t('apiConfiguration')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="integrationName">{t('integrationName')}</Label>
                  <Input
                    id="integrationName"
                    value={formData.integrationName}
                    onChange={(e) => setFormData({ ...formData, integrationName: e.target.value })}
                    placeholder={t('convesio.integration.namePlaceholder')}
                  />
                  <p className="text-muted-foreground mt-1 text-sm">
                    {t('convesioIntegrationNameDescription')}
                  </p>
                </div>
                <div>
                  <Label htmlFor="secretKey">{t('secretKey')}</Label>
                  <Input
                    id="secretKey"
                    type="password"
                    value={formData.secretKey}
                    onChange={(e) => setFormData({ ...formData, secretKey: e.target.value })}
                    placeholder="sk_..."
                  />
                  <p className="text-muted-foreground mt-1 text-sm">
                    {t('convesioSecretKeyDescription')}
                  </p>
                </div>
                <div>
                  <Label htmlFor="webhookSecret">{t('webhookSecret')}</Label>
                  <Input
                    id="webhookSecret"
                    type="password"
                    value={formData.webhookSecret}
                    onChange={(e) => setFormData({ ...formData, webhookSecret: e.target.value })}
                    placeholder="whsec_..."
                  />
                  <p className="text-muted-foreground mt-1 text-sm">
                    {t('convesioWebhookSecretDescription')}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>{t('testModeSettings')}</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="testMode"
                    checked={formData.testMode}
                    onCheckedChange={(checked) => setFormData({ ...formData, testMode: checked === true })}
                  />
                  <Label htmlFor="testMode">{t('enableTestMode')}</Label>
                </div>
                {formData.testMode && (
                  <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3">
                    <p className="text-sm text-yellow-800">
                      {t('convesioTestModeDescription')}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Supported Regions */}
        <Card>
          <CardHeader><CardTitle>{t('supportedRegions')}</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
              {SUPPORTED_COUNTRIES.map((country) => (
                <Badge key={country} variant="secondary" className="justify-center">{country}</Badge>
              ))}
            </div>
            <p className="text-muted-foreground mt-4 text-sm">
              {t('convesioRegionsDescription')}
            </p>
          </CardContent>
        </Card>

        <ProcessorDryModeField
          checked={formData.dryMode}
          onCheckedChange={(checked) => setFormData({ ...formData, dryMode: checked })}
          disabled={updateProcessorMutation.isPending}
        />
      </div>
    </div>
  );
};

export const ConvesioEditPageContainer = withRequiredPermission(ConvesioEditPageContainerInner, PERMISSIONS.PROCESSORS_MANAGE);
export default ConvesioEditPageContainer;
